/**
 * @fileoverview Wrapper reactivo para ProductSelectionModal que se suscribe al sharingInformationService
 * @module RxJProductSelectionModal
 * @category Component - Wrapper
 * @since 1.0.0
 * @version 1.0.0
 */
import React, {
  useEffect,
  useState,
  useRef,
  memo
} from "react";
import { useSelector } from "react-redux";
import { sharingInformationService } from '../../../services/sharingInformationService';
import ProductSelectionModal from './ProductSelectionModal';

/**
 * Wrapper reactivo para ProductSelectionModal que se integra con sharingInformationService.
 *
 * Implementa el patrón **RxJS + React** para manejo de estado global a través del
 * servicio de comunicación entre componentes de la aplicación.
 *
 * @component RxJProductSelectionModal
 * @category Component - Wrapper
 *
 * @description
 * **Características:**
 * - Suscripción reactiva al sharingInformationService
 * - Control automático de apertura/cierre del modal
 * - Manejo de callbacks desde el servicio
 * - Limpieza automática al desmontar el componente
 * - Patrón consistente con otros modales RxJ del sistema
 */
const RxJProductSelectionModal = memo(({ className }) => {
  const [showModal, setShowModal] = useState(false);
  const [originData, setOriginData] = useState({});
  const selectedId = useSelector((state) => state.conversationsReducer.selectedConversationId);
  const lastSelectedIdRef = useRef(selectedId);

  // Suscribirse al sharingInformationService
  useEffect(() => {
    const subscription$ = sharingInformationService.getSubject();

    const myObservable = subscription$.subscribe((data) => {
      // Abrir modal de selección de productos
      if ('openProductSelection' in data) {
        setShowModal(data.openProductSelection);
        setOriginData(data.payload || {});
      }

      // Cerrar modal de selección de productos
      if ('closeProductSelection' in data && data.closeProductSelection) {
        setShowModal(false);
        setOriginData({});
      }
    });

    // Cleanup: desuscribirse al desmontar componente
    return () => {
      myObservable.unsubscribe();
    };
  }, []);

  /**
   * Efecto para notificar cambios de estado del modal al servicio.
   */
  useEffect(() => {
    sharingInformationService.setSubject({
      productSelectionModalOpen: showModal
    });
    sharingInformationService.setSubject({});
    if (showModal) {
      sharingInformationService.setSubject({
        closePaymentLinksForPdf: true,
        closePdfModal: true,
        closeFastAnswersSelection: true
      });
      sharingInformationService.setSubject({});
    }
  }, [showModal]);

  // Cerrar el modal solo si cambia la conversación seleccionada después de estar abierto
  useEffect(() => {
    if (!showModal) {
      lastSelectedIdRef.current = selectedId;
      return;
    }

    if (lastSelectedIdRef.current == null) {
      lastSelectedIdRef.current = selectedId;
      return;
    }

    if (selectedId !== lastSelectedIdRef.current) {
      setShowModal(false);
      setOriginData({});
    }
  }, [selectedId, showModal]);

  /**
   * Función para cerrar el modal.
   *
   * Limpia el estado local y notifica al servicio sobre el cierre.
   */
  const onClose = () => {
    setShowModal(false);
    setOriginData({});
  };

  /**
   * Función para manejar la selección de un producto.
   *
   * Ejecuta el callback configurado desde el servicio y cierra el modal.
   *
   * @function onProductSelect
   * @memberof RxJProductSelectionModal
   * @private
   *
   * @description
   * **Comportamiento:**
   * - Ejecuta callback desde originData si está disponible
   * - Notifica al servicio sobre la selección
   * - Cierra el modal automáticamente
   * - Limpia estado después de la selección
   *
   * @param {Product} product - Producto seleccionado por el usuario
   *
   * @returns {void} No retorna valor, ejecuta callbacks y actualiza estado
   *
   * @example
   * ```javascript
   * // Manejo interno de selección
   * onProductSelect(selectedProduct);
   * ```
   */
  const onProductSelect = (product) => {
    // Ejecutar callback personalizado si existe
    if (originData.onProductSelect && typeof originData.onProductSelect === 'function') {
      originData.onProductSelect(product);
    }

    // Notificar al servicio sobre la selección
    sharingInformationService.setSubject(product);
    sharingInformationService.setSubject({});
  };

  // No renderizar si el modal está cerrado
  if (!showModal) {
    return null;
  }

  return (
    <ProductSelectionModal
      key={`product-selection-modal-${showModal}`}
      onClose={onClose}
      onProductSelect={onProductSelect}
      className={className}
    />
  );
});

/**
 * Nombre de display para debugging y herramientas de desarrollo.
 *
 * @type {string}
 */
RxJProductSelectionModal.displayName = 'RxJProductSelectionModal';

export default RxJProductSelectionModal;
